<template>
  <div>
    <div id="flow">
      <span class="flow-1" />
      <span class="flow-2" />
      <span class="flow-3" />
    </div>
    <div class="container">
      <h1 class="title is-1">
        {{ post.title }}
      </h1>
      <!-- eslint-disable vue/no-v-html -->
      <div class="content" v-html="postHtml" />

      <!-- Links out to the topic hubs. Only tags with a hub page are
           returned by the API, so these never point at a 404. -->
      <nav v-if="hubTags.length" class="post-tags" aria-label="Topics">
        <span class="post-tags__label">Read more on</span>
        <nuxt-link
          v-for="tag in hubTags"
          :key="tag.slug"
          :to="`/blog/tag/${tag.slug}`"
          class="post-tags__pill"
        >
          {{ tag.name }}
        </nuxt-link>
      </nav>
    </div>
    <PromotionBanner />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import PromotionBanner from '@/components/PromotionBanner'
import { getCanonicalUrl } from '@/helpers/seo'
import {
  blogPostingSchema,
  jsonLdScript,
  postBreadcrumbSchema,
} from '@/helpers/blog-schema'
import { buildSocialMeta } from '@/helpers/social-meta'

// Ghost custom fields (og_image, twitter_image) hold a path relative to the
// Ghost admin's own domain, not ours - so they're resolved to an absolute
// Ghost URL here, before buildSocialMeta ever sees them. Its own guard only
// knows how to join a bare path onto pedalsure.com, which would be wrong for
// these.
const GHOST_DOMAIN = 'https://philiplochner.ghost.io'

export default {
  name: 'BlogDetailsPage',
  components: { PromotionBanner },
  async asyncData({ store, params, error }) {
    await store.dispatch('blog/loadSinglePost', params.slug)

    // loadSinglePost swallows failures and leaves singlePost null, which the
    // template can't render — so a bad slug used to crash with a raw 500.
    // A missing post is a real 404; anything else (Ghost down) gets the
    // generic error page instead of a crash.
    if (!store.getters['blog/singlePost']) {
      const status = store.getters['blog/singlePostError']?.response?.status
      return error(
        status === 404
          ? { statusCode: 404, message: 'Blog post not found' }
          : { statusCode: 500, message: 'Blog post could not be loaded' }
      )
    }
  },
  head() {
    // Keyed off Ghost's own slug rather than the layout's route-derived
    // canonical, so a post reached by an alias still points at one URL.
    const path = `/blog/${this.canonicalSlug}`
    const canonicalUrl = getCanonicalUrl(path)
    // Ghost lets an editor set a different title/image per network; each
    // fallback chain picks one CMS-authored value, which is then the single
    // value both tags for that network are built from - never a second,
    // separately-typed string.
    const title = this.post.og_title || this.post.meta_title || this.post.title
    const description =
      this.post.og_description ||
      this.post.twitter_description ||
      this.post.meta_description
    const image = this.post.og_image
      ? `${GHOST_DOMAIN}${this.post.og_image}`
      : this.post.twitter_image
      ? `${GHOST_DOMAIN}${this.post.twitter_image}`
      : this.post.feature_image

    return {
      title: this.post.title,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.post.meta_description,
        },
        { hid: 'title', name: 'title', content: this.post.title },
        { hid: 'keywords', name: 'keywords', content: this.codeInjectedMeta() },
        ...buildSocialMeta({
          title,
          description,
          path,
          image,
          type: 'article',
        }).meta,
      ],
      link: [{ hid: 'canonical', rel: 'canonical', href: canonicalUrl }],
      script: [
        { src: 'https://cdn.embedly.com/widgets/platform.js' },
        jsonLdScript(
          'ld-blogposting',
          blogPostingSchema(this.post, canonicalUrl)
        ),
        jsonLdScript(
          'ld-breadcrumb',
          postBreadcrumbSchema(this.post, this.canonicalSlug)
        ),
      ],
    }
  },
  computed: {
    slug() {
      return this.$route.params.slug
    },
    // Ghost's own slug is the authoritative one, but the store seeds
    // singlePost as {}, so fall back to the route param if the fetch failed —
    // a canonical pointing at /blog/undefined would be worse than none.
    canonicalSlug() {
      return this.post.slug || this.$route.params.slug
    },
    hubTags() {
      return this.post.tags || []
    },
    post() {
      return this.$store.getters['blog/singlePost']
    },
    // Ghost's YouTube cards embed youtube.com, which sets tracking cookies as
    // soon as the iframe loads, before any consent. youtube-nocookie.com is
    // YouTube's own privacy-enhanced host: same player, but it doesn't set
    // YouTube's tracking cookies until the visitor plays the video.
    postHtml() {
      return (this.post.html || '').replace(
        /\/\/(?:www\.)?youtube\.com\/embed\//g,
        '//www.youtube-nocookie.com/embed/'
      )
    },
  },
  beforeMount() {
    setTimeout(() => {
      if (document.getElementById('get-quote-static-button')) {
        document
          .getElementById('get-quote-static-button')
          .addEventListener('click', this.openQuickQuteModal)
      }
    }, 3000)
  },
  methods: {
    ...mapActions('quote', {
      resetQuote: 'resetQuote',
    }),
    openQuickQuteModal() {
      this.resetQuote()
      this.$modal.show('quick-quote')
    },
    codeInjectedMeta() {
      const regex = /<meta name="keywords" content="(.*?)">/gm
      let m
      const keywords = []

      while ((m = regex.exec(this.post.codeinjection_head)) !== null) {
        // This is necessary to avoid infinite loops with zero-width matches
        if (m.index === regex.lastIndex) {
          regex.lastIndex++
        }
        keywords.push(m[1])
      }
      return keywords.join()
    },
  },
}
</script>

<style lang="scss" scoped>
.post-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid var(--ps-border);
}

.post-tags__label {
  font-size: var(--ps-text-label);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--ps-muted);
  margin-right: 4px;
}

.post-tags__pill {
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 0 14px;
  border: 1px solid var(--ps-border);
  border-radius: var(--ps-radius-pill);
  background: var(--ps-white);
  color: var(--ps-body);
  font-size: var(--ps-text-small);
  font-weight: 600;
  text-decoration: none;
  transition: border-color var(--ps-transition), color var(--ps-transition);

  &:hover {
    border-color: var(--ps-indigo-200);
    color: var(--ps-indigo);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--ps-indigo);
    box-shadow: var(--ps-focus-ring);
  }
}

.container {
  max-width: 800px;
  padding: 20px;
  backdrop-filter: blur(20px);
  @include tablet {
    padding: 50px;
  }
}
</style>
